import { normalizeCountryCode } from "@/lib/countries";

export interface DuplicateCandidate {
  id: string;
  name: string;
  organization_number: string | null;
  country_code: string | null;
}

export interface CustomerDuplicate<C extends DuplicateCandidate> {
  /** `org`: samme org.nr og land, blokkerer opprettelse. `name`: bare samme navn, et varsel. */
  kind: "org" | "name";
  customer: C;
}

/** Bare sifrene, samme regel som `convert_lead_to_customer` i SQL. */
export function normalizeOrgNumber(raw: string | null | undefined): string {
  return (raw ?? "").replace(/\D/g, "");
}

function normalizeName(raw: string | null | undefined): string {
  return (raw ?? "").trim().toLowerCase().replace(/\s+/g, " ");
}

/**
 * Finner en eksisterende kunde som en ny kunde ville vært en dublett av.
 *
 * Org.nr-treff blokkerer: `customers.poweroffice_customer_id` er UNIQUE, så en
 * dublett av en koblet kunde matcher samme PO-kunde i `ensure-po-customer` og
 * blir stående ukoblet. Land må være likt, fordi utenlandsk VAT kan ha samme
 * sifre som et norsk org.nr. Et norsk org.nr teller først når det har 9 sifre.
 * Navn er ikke unike, så navnetreff er bare et varsel.
 */
export function findDuplicateCustomer<C extends DuplicateCandidate>(
  customers: readonly C[],
  input: { organizationNumber: string; name: string; countryCode: string },
): CustomerDuplicate<C> | null {
  const country = normalizeCountryCode(input.countryCode);
  const org = normalizeOrgNumber(input.organizationNumber);
  const orgComplete = country === "NO" ? org.length === 9 : org.length > 0;
  if (orgComplete) {
    const hit = customers.find(
      (c) => normalizeOrgNumber(c.organization_number) === org && normalizeCountryCode(c.country_code) === country,
    );
    if (hit) return { kind: "org", customer: hit };
  }

  const name = normalizeName(input.name);
  if (name) {
    const hit = customers.find((c) => normalizeName(c.name) === name);
    if (hit) return { kind: "name", customer: hit };
  }
  return null;
}
